import React from 'react';
import { Card } from '../../../../../../components/ui/card';
import { 
  Calendar,
  MapPin,
  User,
  Building2,
  Activity,
  FileText,
} from 'lucide-react';
import type { DashboardProject } from '../../../../types/project';

interface BasicInfoProps {
  project: DashboardProject;
}

export const BasicInfo: React.FC<BasicInfoProps> = ({ project }) => {
  const getStatusColor = (status: DashboardProject['status']) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'completed': 'bg-blue-100 text-blue-800',
      'on-hold': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800'
    } as const;
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 gap-2" style={{ margin: '20px' }}>
      <Card className="p-6 overflow-hidden">
        <h3 className="text-xl font-semibold mb-6">Project Information</h3>
        <div className="grid grid-cols-1 gap-y-6">
          {/* Using a consistent 12-column grid for both rows */}
          <div className="grid grid-cols-12 gap-4 lg:gap-8 overflow-hidden">
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <Building2 className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Project Name</p>
                <p className="text-base font-semibold text-gray-900 break-words truncate">{project.projectName}</p>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <FileText className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Project Number</p>
                <p className="text-base font-semibold text-gray-900 break-words truncate">{project.projectNumber}</p>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <User className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Customer</p>
                <p className="text-base font-semibold text-gray-900 break-words truncate">{project.customer}</p>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <MapPin className="h-4 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Location</p>
                <p className="text-base font-semibold text-gray-900 break-words truncate">{project.location}</p>
              </div>
            </div>
          </div>
          
          {/* Second Row - using the same 12-column grid */}
          <div className="grid grid-cols-12 gap-4 lg:gap-8 overflow-hidden">
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <Activity className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                  {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <Calendar className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">Start Date</p>
                <p className="text-base font-semibold text-gray-900 break-words">{formatDate(project.startDate)}</p>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3 flex items-center min-w-0">
              <Calendar className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
              <div className="min-w-0 ml-4 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-500 mb-1">End Date</p>
                <p className="text-base font-semibold text-gray-900 break-words">{formatDate(project.endDate)}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}; 